import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PlayArrow, Pause, ContentCut } from '@mui/icons-material';
import { Box, Typography, IconButton, Paper, Button } from '@mui/material';

import { MP3Compressor } from '@shared/utils/mp3-compression';
import { audioBufferToWav } from '@shared/utils/audio-utils';

interface WaveformTrimmerProps {
  audioFile?: File | string | null;
  fileName?: string;
  onTrimChange?: (startTime: number, endTime: number) => void;
  onTrimmedFileChange?: (file: File) => void;
  disabled?: boolean;
}

interface WaveformPoint {
  x: number;
  y: number;
}

const WaveformTrimmer: React.FC<WaveformTrimmerProps> = ({
  audioFile,
  fileName,
  onTrimChange,
  onTrimmedFileChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [waveformData, setWaveformData] = useState<WaveformPoint[]>([]);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'playhead' | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);
  const [trimmedAudioUrl, setTrimmedAudioUrl] = useState<string | null>(null);
  const [trimProgress, setTrimProgress] = useState<{
    stage: string;
    progress: number;
  }>({ stage: '', progress: 0 });


  // Реальный анализ аудиофайла для создания вейвформа
  const analyzeAudioFile = useCallback(async (file: File): Promise<WaveformPoint[]> => {
    return new Promise((resolve) => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          const channelData = audioBuffer.getChannelData(0); // Берем первый канал
          const samples = channelData.length;
          const blockSize = Math.floor(samples / 800); // 800 точек для вейвформа
          const filteredData = [];
          
          for (let i = 0; i < 800; i++) {
            const blockStart = blockSize * i;
            let sum = 0;
            
            for (let j = 0; j < blockSize; j++) {
              sum += Math.abs(channelData[blockStart + j]);
            }
            
            const average = sum / blockSize;
            filteredData.push(average);
          }
          
          // Нормализация данных
          const maxValue = Math.max(...filteredData);
          const normalizedData = filteredData.map(value => value / maxValue);
          
          // Создание точек вейвформа
          const points: WaveformPoint[] = normalizedData.map((amplitude, index) => ({
            x: index,
            y: 50 - (amplitude * 40) // Инвертируем Y координату для правильного отображения
          }));
          
          audioContext.close();
          resolve(points);
        } catch (error) {
          console.error('Ошибка анализа аудио:', error);
          // В случае ошибки возвращаем пустой вейвформ
          resolve([]);
        }
      };
      
      reader.readAsArrayBuffer(file);
    });
  }, []);

  // Определяем, является ли устройство мобильным
  const isMobile = useCallback(() => {
    return window.innerWidth <= 768;
  }, []);

  // Проверяем поддержку формата аудио
  const checkAudioFormatSupport = useCallback((audioElement: HTMLAudioElement): boolean => {
    if (!audioElement.src) return false;
    
    const audio = document.createElement('audio');
    const canPlay = audio.canPlayType(audioElement.src.includes('mp3') ? 'audio/mpeg' : 
                                     audioElement.src.includes('webm') ? 'audio/webm' :
                                     audioElement.src.includes('ogg') ? 'audio/ogg' :
                                     audioElement.src.includes('wav') ? 'audio/wav' : 'audio/mpeg');
    
    console.log('Проверка поддержки формата:', {
      src: audioElement.src,
      canPlay: canPlay,
      format: audioElement.src.split('.').pop()
    });
    
    return canPlay !== '';
  }, []);

  // Функция для отрисовки ползунка с учетом мобильной версии
  const drawSlider = useCallback((ctx: CanvasRenderingContext2D, x: number, height: number, color: string, type: 'start' | 'end') => {
    const isMobileView = isMobile();
    const lineWidth = isMobileView ? 12 : 6; // Ширина линии
    const handleRadius = isMobileView ? 15 : 8; // Размер основной ручки
    const handleOffset = isMobileView ? 25 : 15; // Отступ от края
    
    // Рисуем основную линию ползунка
    ctx.fillStyle = color;
    ctx.fillRect(x - lineWidth/2, 0, lineWidth, height);
    
    // Рисуем тень для объема
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(x - lineWidth/3, 0, lineWidth/1.5, height);
    
 
    

    
    
    
  }, [isMobile]);

  // Функция для отрисовки позиции воспроизведения
  const drawPlayhead = useCallback((ctx: CanvasRenderingContext2D, x: number, height: number) => {
    const isMobileView = isMobile();
    const handleRadius = isMobileView ? 12 : 6;
    const lineWidth = isMobileView ? 3 : 2;
    const handleOffset = isMobileView ? 25 : 15;
    
    // Рисуем линию позиции
    ctx.strokeStyle = '#FF6B6B';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
    ctx.setLineDash([]);

    
    
    

   


    
  }, [isMobile]);

  // Отрисовка вейвформа
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvas;
    
    // Очищаем канвас
    ctx.clearRect(0, 0, width, height);
    
    // Рисуем фон
    ctx.fillStyle = '#304268';
    ctx.fillRect(0, 0, width, height);
    
    // Рисуем вейвформ
    ctx.beginPath();
    ctx.strokeStyle = '#80FFB3';
    ctx.lineWidth = 2;
    
    // Рисуем основную линию вейвформа
    waveformData.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    
    ctx.stroke();
    
    // Рисуем заполненную область
    ctx.beginPath();
    ctx.fillStyle = '#80FFB3';
    ctx.globalAlpha = 0.3;
    
    // Верхняя часть (симметричная)
    waveformData.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    
    // Создаем симметричную нижнюю часть
    for (let i = waveformData.length - 1; i >= 0; i--) {
      const point = waveformData[i];
      const mirroredY = height - (point.y - height/2); // Симметричное отражение
      ctx.lineTo(point.x, mirroredY);
    }
    
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
    
    // Рисуем затемненные области вне выбранного отрезка
    const startX = (startTime / duration) * width;
    const endX = (endTime / duration) * width;
    
    // Левая затемненная область
    if (startX > 0) {
      ctx.fillStyle = '#1a2332';
      ctx.globalAlpha = 0.7;
      ctx.fillRect(0, 0, startX, height);
    }
    
    // Правая затемненная область
    if (endX < width) {
      ctx.fillStyle = '#1a2332';
      ctx.globalAlpha = 0.7;
      ctx.fillRect(endX, 0, width - endX, height);
    }
    ctx.globalAlpha = 1;
    
    // Рисуем ползунки
    drawSlider(ctx, startX, height, '#70D9FF', 'start');
    drawSlider(ctx, endX, height, '#70D9FF', 'end');
    
    // Рисуем текущую позицию воспроизведения
    const currentX = (currentTime / duration) * width;
    if (currentTime > 0 && currentTime <= duration) {
      drawPlayhead(ctx, currentX, height);
    }
  }, [waveformData, startTime, endTime, duration, currentTime, drawPlayhead, drawSlider]);

  // Обработка кликов по канвасу
  const handleCanvasMouseDown = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const clickTime = (x / canvas.width) * duration;
    
    // Клик по вейвформу - устанавливаем текущее время
    const newTime = Math.max(0, Math.min(duration, clickTime));
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      if (isPlaying) {
        audioRef.current.play();
      }
    }
  }, [duration, isPlaying, disabled]);

  // Обработка начала перетаскивания ручек
  const handleHandleMouseDown = useCallback((event: React.MouseEvent, type: 'start' | 'end' | 'playhead') => {
    event.stopPropagation();
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    
    if (type === 'start') {
      setIsDragging('start');
      setDragOffset(x - ((startTime / duration) * canvas.width));
    } else if (type === 'end') {
      setIsDragging('end');
      setDragOffset(x - ((endTime / duration) * canvas.width));
    } else if (type === 'playhead') {
      setIsDragging('playhead');
      setDragOffset(x - ((currentTime / duration) * canvas.width));
    }
  }, [duration, startTime, endTime, currentTime, disabled]);

  // Обработка перетаскивания
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || disabled) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left - dragOffset;
    const newTime = Math.max(0, Math.min(duration, (x / canvas.width) * duration));
    
    if (isDragging === 'start') {
      const newStartTime = Math.min(newTime, endTime - 0.1);
      setStartTime(newStartTime);
      onTrimChange?.(newStartTime, endTime);
      
      // Если воспроизведение активно, переключаемся на новую начальную точку
      if (isPlaying && audioRef.current) {
        audioRef.current.currentTime = newStartTime;
      }
    } else if (isDragging === 'end') {
      const newEndTime = Math.max(newTime, startTime + 0.1);
      setEndTime(newEndTime);
      onTrimChange?.(startTime, newEndTime);
      
      // Если воспроизведение активно и достигли нового конца, останавливаем
      if (isPlaying && audioRef.current && audioRef.current.currentTime >= newEndTime) {
        audioRef.current.pause();
        setIsPlaying(false);
        audioRef.current.currentTime = startTime;
      }
    } else if (isDragging === 'playhead') {
      // Перетаскивание позиции воспроизведения
      // Устанавливаем время без ограничений, но останавливаем воспроизведение если вышли за пределы
      setCurrentTime(newTime);
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
        
        // Если вышли за пределы выбранного диапазона, останавливаем воспроизведение
        if (isPlaying && (newTime < startTime || newTime > endTime)) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
    }
  }, [isDragging, dragOffset, duration, startTime, endTime, onTrimChange, disabled, isPlaying]);

  // Обработка отпускания мыши
  const handleMouseUp = useCallback(() => {
    if (isDragging === 'playhead') {
      // Если отпустили за пределами диапазона, возвращаем к ближайшей границе
      if (currentTime < startTime) {
        setCurrentTime(startTime);
        if (audioRef.current) {
          audioRef.current.currentTime = startTime;
        }
      } else if (currentTime > endTime) {
        setCurrentTime(endTime);
        if (audioRef.current) {
          audioRef.current.currentTime = endTime;
        }
      }
    }
    setIsDragging(null);
  }, [isDragging, currentTime, startTime, endTime]);

  // Обработка выхода мыши за пределы канваса
  const handleMouseLeave = useCallback(() => {
    if (isDragging === 'playhead') {
      // При выходе за пределы канваса, возвращаем к ближайшей границе
      if (currentTime < startTime) {
        setCurrentTime(startTime);
        if (audioRef.current) {
          audioRef.current.currentTime = startTime;
        }
      } else if (currentTime > endTime) {
        setCurrentTime(endTime);
        if (audioRef.current) {
          audioRef.current.currentTime = endTime;
        }
      }
    }
    setIsDragging(null);
  }, [isDragging, currentTime, startTime, endTime]);

  // Обработка воспроизведения
  const togglePlayback = useCallback(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Проверяем, загружен ли аудиофайл
      if (!audioRef.current.src) {
        console.error('Аудио источник не установлен');
        return;
      }

      // Проверяем поддержку формата
      if (!checkAudioFormatSupport(audioRef.current)) {
        console.error('Формат аудио не поддерживается браузером');
        return;
      }

      // Проверяем длительность
      if (!duration || duration <= 0) {
        console.error('Длительность аудио не установлена');
        return;
      }

      // Если воспроизведение начинается, устанавливаем позицию на начальную точку
      if (audioRef.current.currentTime < startTime || audioRef.current.currentTime >= endTime) {
        audioRef.current.currentTime = startTime;
      }
      
      // Добавляем обработку ошибок воспроизведения
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            console.log('Воспроизведение началось успешно, текущее время:', audioRef.current?.currentTime);
          })
          .catch((error) => {
            console.error('Ошибка воспроизведения:', error);
            console.error('Детали ошибки воспроизведения:', {
              error: error,
              audioSrc: audioRef.current?.src,
              audioNetworkState: audioRef.current?.networkState,
              audioReadyState: audioRef.current?.readyState,
              audioError: audioRef.current?.error
            });
            setIsPlaying(false);
          });
      }
    }
  }, [isPlaying, startTime, endTime, duration, checkAudioFormatSupport]);

  // Обновление времени воспроизведения
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Проверяем, что аудио действительно воспроизводится
      if (!audio.paused && audio.currentTime >= endTime) {
        audio.pause();
        setIsPlaying(false);
        audio.currentTime = startTime;
      }
    };

    const handleError = (e: ErrorEvent) => {
      console.error('Ошибка аудио:', e);
      console.error('Детали ошибки:', {
        type: e.type,
        target: e.target,
        currentSrc: audio.src,
        networkState: audio.networkState,
        readyState: audio.readyState,
        error: audio.error
      });
      setIsPlaying(false);
      
      // Пробуем перезагрузить аудио только если это не ошибка формата
      if (audio.error && audio.error.code !== MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        console.log('Пробуем перезагрузить аудио...');
        audio.load();
      } else {
        console.error('Формат аудио не поддерживается браузером');
      }
    };

    const handleCanPlay = () => {
      console.log('Аудио готово к воспроизведению');
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [startTime, endTime]);

  // Функция для получения длительности аудио через Web Audio API
  const getAudioDuration = async (audioData: Blob): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          const arrayBuffer = reader.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          resolve(audioBuffer.duration);
          audioContext.close();
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Ошибка чтения файла'));
      reader.readAsArrayBuffer(audioData);
    });
  };

  // Инициализация при загрузке аудио
  useEffect(() => {
    console.log('🎵 WaveformTrimmer - audioFile changed:'
      , {
      audioFile,
      audioFileType: typeof audioFile,
      isFile: audioFile instanceof File,
      isString: typeof audioFile === 'string',
      fileName
    });
    
    if (!audioFile) {
      // console.log('🎵 WaveformTrimmer - no audioFile, resetting state');
      setDuration(0);
      setStartTime(0);
      setEndTime(0);
      setWaveformData([]);
      return;
    }
    
    const audio = audioRef.current;
    if (!audio) return;
    
    let fileToAnalyze = audioFile;
    let audioUrl = '';
    
    const initializeAudio = async () => {
      // console.log('🎵 WaveformTrimmer - initializeAudio started');
      try {
        if (typeof audioFile === 'string') {
          // console.log('🎵 WaveformTrimmer - processing string URL:', audioFile);
          // Загружаем аудио файл
          const response = await fetch(audioFile, {
            mode: 'cors',
            credentials: 'same-origin'
          });
          
          if (!response.ok) {
            throw new Error(`Ошибка загрузки аудио данных: ${response.status}`);
          }
          
          const blob = await response.blob();
          
          // Получаем длительность через Web Audio API
          // console.log('Получаем длительность через Web Audio API...');
          const audioDuration = await getAudioDuration(blob);
          console.log('Получена длительность:', audioDuration);
          
          if (!isFinite(audioDuration) || audioDuration <= 0) {
            throw new Error('Получена невалидная длительность аудио');
          }
          
          // Создаем временный URL и устанавливаем длительность
          const tempUrl = URL.createObjectURL(blob);
          audioUrl = tempUrl;
          audio.src = tempUrl;
          
          // Устанавливаем длительность сразу
          setDuration(audioDuration);
          setStartTime(0);
          setEndTime(audioDuration);
          
          // Анализируем аудио для вейвформа
          setIsAnalyzing(true);
          const tempFile = new File([blob], 'audio.mp3', { type: 'audio/mpeg' });
          const data = await analyzeAudioFile(tempFile);
          setWaveformData(data);
          setIsAnalyzing(false);
          
        } else {
          // Для File объекта
          const blob = await audioFile.slice().arrayBuffer().then(buffer => new Blob([buffer], { type: audioFile.type }));
          
          // Получаем длительность через Web Audio API
          console.log('Получаем длительность через Web Audio API...');
          const audioDuration = await getAudioDuration(blob);
          console.log('Получена длительность:', audioDuration);
          
          if (!isFinite(audioDuration) || audioDuration <= 0) {
            throw new Error('Получена невалидная длительность аудио');
          }
          
          // Создаем URL и устанавливаем аудио
          const url = URL.createObjectURL(blob);
          audioUrl = url;
          audio.src = url;
          
          // Устанавливаем длительность сразу
          setDuration(audioDuration);
          setStartTime(0);
          setEndTime(audioDuration);
          
          // Анализируем для вейвформа
          setIsAnalyzing(true);
          const data = await analyzeAudioFile(audioFile);
          setWaveformData(data);
          setIsAnalyzing(false);
        }

        // Добавляем обработчики событий для мониторинга
        const handleCanPlayThrough = () => {
          console.log('Аудио готово к воспроизведению, duration из элемента:', audio.duration);
        };

        const handleError = (e: ErrorEvent) => {
          console.error('Ошибка аудио при инициализации:', e);
          console.error('Детали ошибки:', {
            type: e.type,
            target: e.target,
            currentSrc: audio.src,
            networkState: audio.networkState,
            readyState: audio.readyState,
            error: audio.error
          });
        };

        audio.addEventListener('canplaythrough', handleCanPlayThrough);
        audio.addEventListener('error', handleError);
        
        return () => {
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('error', handleError);
          if (audioUrl && audioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(audioUrl);
          }
        };
      } catch (error) {
        console.error('Ошибка инициализации аудио:', error);
        setIsAnalyzing(false);
      }
    };

    initializeAudio();
  }, [audioFile, analyzeAudioFile]);

  // Отрисовка при изменении данных
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  // Форматирование времени
  const formatTime = (time: number) => {
    if (!isFinite(time) || isNaN(time)) {
      return '00:00.0';
    }
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const tenths = Math.floor((time % 1) * 10);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  };

  // Функция обрезки аудио
  const handleTrimAudio = useCallback(async () => {
    if (!audioRef.current || !audioFile) {
      console.log('Нет аудиофайла или аудио элемента');
      return;
    }
    
    // Останавливаем текущее воспроизведение
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    
    console.log('Начинаем обрезку аудио...');
    setIsTrimming(true);
    setTrimProgress({ stage: t('trimmer.preparing'), progress: 0 });
    
    try {
      // Создаем AudioContext для обрезки
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('AudioContext создан');
      
      // Получаем аудио данные
      let arrayBuffer: ArrayBuffer;
      let originalFileName: string;
      
      setTrimProgress({ stage: t('trimmer.loading'), progress: 5 });
      
      if (typeof audioFile === 'string') {
        // Загружаем по URL
        console.log('Загружаем аудио по URL:', audioFile);
        const response = await fetch(audioFile);
        arrayBuffer = await response.arrayBuffer();
        // Получаем оригинальное имя файла из URL
        originalFileName = decodeURIComponent(audioFile.split('/').pop() || 'audio');
        console.log('Аудио загружено, размер:', arrayBuffer.byteLength, 'байт');
      } else {
        // Читаем File
        console.log('Читаем File:', audioFile.name);
        arrayBuffer = await audioFile.arrayBuffer();
        originalFileName = audioFile.name;
        console.log('File прочитан, размер:', arrayBuffer.byteLength, 'байт');
      }
      
      setTrimProgress({ stage: t('trimmer.decoding'), progress: 15 });
      
      // Формируем имя файла
      const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
      const cleanFileName = fileNameWithoutExt
        .replace(/_trimmed_[a-z0-9]+$/, '')
        .replace(/__+/g, '_');
      
      const trimmedFileName = `${cleanFileName}.mp3`;
      
      // Декодируем аудио
      console.log('Декодируем аудио...');
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
      
      setTrimProgress({ stage: t('trimmer.trimming'), progress: 25 });
      
      // Вычисляем сэмплы для обрезки
      const sampleRate = audioBuffer.sampleRate;
      const startSample = Math.floor(startTime * sampleRate);
      const endSample = Math.floor(endTime * sampleRate);
      const trimmedLength = endSample - startSample;
      
      console.log('Параметры обрезки:', {
        startTime,
        endTime,
        startSample,
        endSample,
        trimmedLength,
        sampleRate
      });
      
      // Создаем новый AudioBuffer для обрезанного аудио
      const trimmedBuffer = audioContext.createBuffer(
        audioBuffer.numberOfChannels,
        trimmedLength,
        sampleRate
      );
      console.log('Создан trimmedBuffer, каналов:', trimmedBuffer.numberOfChannels);
      
      // Копируем данные из выбранного диапазона с прогрессом
      setTrimProgress({ stage: t('trimmer.copying'), progress: 35 });
      
      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        const originalData = audioBuffer.getChannelData(channel);
        const trimmedData = trimmedBuffer.getChannelData(channel);
        
        // Обновляем прогресс для каждого канала
        const channelProgress = 35 + (channel / audioBuffer.numberOfChannels) * 15;
        setTrimProgress({ stage: t('trimmer.copying'), progress: channelProgress });
        
        for (let i = 0; i < trimmedLength; i++) {
          trimmedData[i] = originalData[startSample + i];
        }
      }
      console.log('Данные скопированы в trimmedBuffer');
      
      setTrimProgress({ stage: t('trimmer.encoding'), progress: 55 });
      
      // Конвертируем в MP3 используя MP3Compressor
      console.log('Начинаем конвертацию в MP3...');
      const compressor = new MP3Compressor();
      
      // Сохраняем оригинальное название файла для временного файла
      const tempOriginalFileName = typeof audioFile === 'string' ? 'audio' : audioFile.name;
      const tempFileNameWithoutExt = tempOriginalFileName.replace(/\.[^/.]+$/, '');
      
      // Создаем WAV файл из AudioBuffer
      const wavBlob = await audioBufferToWav(trimmedBuffer);
      const tempFile = new File([wavBlob], `${tempFileNameWithoutExt}.wav`, { type: 'audio/wav' });
      
      const compressionResult = await compressor.compressMP3(tempFile, (progress) => {
        // Прогресс от 55% до 85%
        const encodingProgress = 55 + (progress.progress * 0.3);
        setTrimProgress({ stage: t('trimmer.encoding'), progress: encodingProgress });
      });
      
      console.log('MP3 файл создан, размер:', compressionResult.compressedFile.size, 'байт');
      
      setTrimProgress({ stage: t('trimmer.finishing'), progress: 90 });
      
      const trimmedUrl = URL.createObjectURL(compressionResult.compressedFile);
      console.log('URL создан для обрезанного аудио');
      
      setTrimmedAudioUrl(trimmedUrl);
      
      // Используем сжатый файл напрямую
      const trimmedFile = compressionResult.compressedFile;
      console.log('File объект создан:', trimmedFile.name, 'размер:', trimmedFile.size);
      
      // Передаем обрезанный файл в родительский компонент
      onTrimmedFileChange?.(trimmedFile);
      
      setTrimProgress({ stage: t('trimmer.updating'), progress: 95 });
      
      // Обновляем вейвформ для обрезанного аудио
      console.log('Обновляем вейвформ...');
      
      // Анализируем обрезанный буфер напрямую
      const channelData = trimmedBuffer.getChannelData(0); // Берем первый канал
      const samples = channelData.length;
      const blockSize = Math.floor(samples / 800); // 800 точек для вейвформа
      const filteredData = [];
      
      for (let i = 0; i < 800; i++) {
        const blockStart = blockSize * i;
        let sum = 0;
        
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[blockStart + j]);
        }
        
        const average = sum / blockSize;
        filteredData.push(average);
      }
      
      // Нормализация данных
      const maxValue = Math.max(...filteredData);
      const normalizedData = filteredData.map(value => value / maxValue);
      
      // Создание точек вейвформа
      const newWaveformData: WaveformPoint[] = normalizedData.map((amplitude, index) => ({
        x: index,
        y: 50 - (amplitude * 40) // Инвертируем Y координату для правильного отображения
      }));
      
      setWaveformData(newWaveformData);
      console.log('Вейвформ обновлен, точек:', newWaveformData.length);
      
      // Обновляем время - используем реальную длительность обрезанного аудио
      const trimmedDuration = trimmedBuffer.duration;
      console.log('trimmedBuffer.duration:', trimmedDuration);
      console.log('trimmedBuffer.length:', trimmedBuffer.length);
      console.log('trimmedBuffer.numberOfChannels:', trimmedBuffer.numberOfChannels);
      console.log('trimmedBuffer.sampleRate:', trimmedBuffer.sampleRate);
      
      console.log('Устанавливаем duration:', trimmedDuration);
      setDuration(trimmedDuration);
      console.log('Устанавливаем startTime: 0');
      setStartTime(0);
      console.log('Устанавливаем endTime:', trimmedDuration);
      setEndTime(trimmedDuration);
      console.log('Устанавливаем currentTime: 0');
      setCurrentTime(0);
      console.log('Время обновлено, новая длительность:', trimmedDuration);
      
      // Обновляем аудио элемент
      if (audioRef.current) {
        audioRef.current.src = trimmedUrl;
        audioRef.current.currentTime = 0;
        console.log('Аудио элемент обновлен');
      }
      
      audioContext.close();
      console.log('Обрезка завершена успешно!');
      
      setTrimProgress({ stage: t('trimmer.done'), progress: 100 });
      
    } catch (error) {
      console.error('Ошибка обрезки аудио:', error);
      setTrimProgress({ stage: t('trimmer.error'), progress: 0 });
    } finally {
      setIsTrimming(false);
      // Сбрасываем прогресс через 3 секунды после завершения
      setTimeout(() => {
        setTrimProgress({ stage: '', progress: 0 });
      }, 3000);
      console.log('Состояние обрезки сброшено');
    }
  }, [audioFile, startTime, endTime, analyzeAudioFile, onTrimmedFileChange, isPlaying, t]);



  // Функция для декодирования имени файла
  const decodeFileName = (fileName: string): string => {
    try {
      // Сначала пробуем декодировать URL
      const decodedUrl = decodeURIComponent(fileName);
      
      // Извлекаем имя файла из пути
      const nameMatch = decodedUrl.match(/[^/]*$/);
      if (nameMatch) {
        // Заменяем подчеркивания на пробелы, но сохраняем двойные подчеркивания как тире
        return nameMatch[0]
          .replace(/_trimmed_[a-z0-9]+(?=\.mp3$)/, '') // Убираем суффикс trimmed
          .replace(/__/g, ' - ') // Заменяем двойные подчеркивания на тире
          .replace(/_/g, ' '); // Заменяем одинарные подчеркивания на пробелы
      }
      return decodedUrl;
    } catch (error) {
      console.error('Error decoding filename:', error);
      return fileName;
    }
  };

  // Обработчик начала перетаскивания ручек
  const handleHandleTouch = useCallback((event: React.TouchEvent, type: 'start' | 'end' | 'playhead') => {
    event.preventDefault(); // Предотвращаем скролл
    event.stopPropagation();
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const touch = event.touches[0];
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    
    if (type === 'start') {
      setIsDragging('start');
      setDragOffset(x - ((startTime / duration) * canvas.width));
    } else if (type === 'end') {
      setIsDragging('end');
      setDragOffset(x - ((endTime / duration) * canvas.width));
    } else if (type === 'playhead') {
      setIsDragging('playhead');
      setDragOffset(x - ((currentTime / duration) * canvas.width));
    }
  }, [duration, startTime, endTime, currentTime, disabled]);

  // Обработчик движения мыши/тача на уровне документа
  const handleGlobalMove = useCallback((event: MouseEvent | TouchEvent) => {
    if (!isDragging || disabled) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const x = Math.max(0, Math.min(canvas.width, clientX - rect.left - dragOffset));
    const newTime = (x / canvas.width) * duration;
    
    if (isDragging === 'start') {
      const newStartTime = Math.min(newTime, endTime - 0.1);
      setStartTime(newStartTime);
      onTrimChange?.(newStartTime, endTime);
      
      if (isPlaying && audioRef.current) {
        audioRef.current.currentTime = newStartTime;
      }
    } else if (isDragging === 'end') {
      const newEndTime = Math.max(newTime, startTime + 0.1);
      setEndTime(newEndTime);
      onTrimChange?.(startTime, newEndTime);
      
      if (isPlaying && audioRef.current && audioRef.current.currentTime >= newEndTime) {
        audioRef.current.pause();
        setIsPlaying(false);
        audioRef.current.currentTime = startTime;
      }
    } else if (isDragging === 'playhead') {
      setCurrentTime(newTime);
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
        if (isPlaying && (newTime < startTime || newTime > endTime)) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
    }
  }, [isDragging, dragOffset, duration, startTime, endTime, onTrimChange, disabled, isPlaying]);

  // Обработчик отпускания на уровне документа
  const handleGlobalEnd = useCallback(() => {
    setIsDragging(null);
  }, []);

  // Добавляем и удаляем глобальные обработчики
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMove);
      document.addEventListener('mouseup', handleGlobalEnd);
      document.addEventListener('touchmove', handleGlobalMove);
      document.addEventListener('touchend', handleGlobalEnd);
      document.addEventListener('touchcancel', handleGlobalEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMove);
        document.removeEventListener('mouseup', handleGlobalEnd);
        document.removeEventListener('touchmove', handleGlobalMove);
        document.removeEventListener('touchend', handleGlobalEnd);
        document.removeEventListener('touchcancel', handleGlobalEnd);
      };
    }
  }, [isDragging, handleGlobalMove, handleGlobalEnd]);

  return (
    <Box sx={{ 
      backgroundColor: '#283757',
      color: 'white',
      borderRadius: 2,
      p: 2
    }}>
      {/* Название файла */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#70D9FF', mb: 1 }}>
          {fileName || (audioFile ? (typeof audioFile === 'string' ? decodeFileName(audioFile) : decodeFileName(audioFile.name)) : t('trimmer.selectFile'))}
        </Typography>
        {isAnalyzing && (
          <Typography variant="body2" sx={{ color: '#80FFB3', fontSize: '0.8rem' }}>
            {t('trimmer.analyzing')}
          </Typography>
        )}
        {trimmedAudioUrl && (
          <Typography variant="body2" sx={{ color: '#FFD700', fontSize: '0.8rem' }}>
            ✓ {t('trimmer.audioTrimmed')}
          </Typography>
        )}
        {/* Прогресс обрезки */}
        {trimProgress.stage && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: '#FFD700', fontSize: '0.8rem', mb: 0.5 }}>
              {trimProgress.stage}
            </Typography>
            <Box sx={{ 
              width: '100%', 
              height: '4px', 
              backgroundColor: '#1a2332',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                width: `${trimProgress.progress}%`, 
                height: '100%', 
                backgroundColor: '#FFD700',
                transition: 'width 0.3s ease'
              }} />
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Вейвформ с ручками управления */}
      <Box sx={{ 
        position: 'relative', 
        mb: 1,
        pt: isMobile() ? 2.5 : 2,
        pb: isMobile() ? 2.5 : 2
      }}>
        {/* Канвас с вейвформом */}
        <canvas
          ref={canvasRef}
          width={800}
          height={isMobile() ? 150 : 100}
          style={{
            width: '100%',
            height: isMobile() ? '150px' : '100px',
            cursor: isDragging ? 'grabbing' : 'grab',
            borderRadius: '8px',
            opacity: isAnalyzing ? 0.5 : 1,
            touchAction: 'none'
          }}
          onMouseDown={handleCanvasMouseDown}
        />

        {/* Кнопка воспроизведения в центре */}
        <Box sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10
        }}>
          <IconButton
            onClick={togglePlayback}
            disabled={disabled || isAnalyzing || !audioFile}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#283757',
              width: isMobile() ? '48px' : '36px',
              height: isMobile() ? '48px' : '36px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
        </Box>

        {/* Ручка воспроизведения сверху */}
        <Box
          onMouseDown={(e) => handleHandleMouseDown(e, 'playhead')}
          onTouchStart={(e) => handleHandleTouch(e, 'playhead')}
          sx={{
            position: 'absolute',
            top: isMobile() ? '-20px' : '-16px',
            left: `${(currentTime / duration) * 100}%`,
            transform: 'translateX(-50%)',
            width: isMobile() ? '32px' : '24px',
            height: isMobile() ? '32px' : '24px',
            backgroundColor: '#FF6B6B',
            borderRadius: '50%',
            border: '2px solid white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            zIndex: 10,
            '&::after': {
              content: '""',
              width: 0,
              height: 0,
              borderLeft: isMobile() ? '8px solid transparent' : '6px solid transparent',
              borderRight: isMobile() ? '8px solid transparent' : '6px solid transparent',
              borderTop: isMobile() ? '8px solid white' : '6px solid white',
              marginTop: '2px'
            }
          }}
        />

        {/* Ручка начала обрезки снизу */}
        <Box
          onMouseDown={(e) => handleHandleMouseDown(e, 'start')}
          onTouchStart={(e) => handleHandleTouch(e, 'start')}
          sx={{
            position: 'absolute',
            // Уменьшаем отступ снизу
            bottom: isMobile() ? '-20px' : '-16px',
            left: `${(startTime / duration) * 100}%`,
            transform: 'translateX(-50%)',
            width: isMobile() ? '32px' : '24px',
            height: isMobile() ? '32px' : '24px',
            backgroundColor: '#70D9FF',
            borderRadius: '50%',
            border: '2px solid white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            zIndex: 10,
            '&::after': {
              content: '"◀"',
              color: 'white',
              fontSize: isMobile() ? '16px' : '12px',
              fontWeight: 'bold'
            }
          }}
        />

        {/* Ручка конца обрезки снизу */}
        <Box
          onMouseDown={(e) => handleHandleMouseDown(e, 'end')}
          onTouchStart={(e) => handleHandleTouch(e, 'end')}
          sx={{
            position: 'absolute',
            // Уменьшаем отступ снизу
            bottom: isMobile() ? '-20px' : '-16px',
            left: `${(endTime / duration) * 100}%`,
            transform: 'translateX(-50%)',
            width: isMobile() ? '32px' : '24px',
            height: isMobile() ? '32px' : '24px',
            backgroundColor: '#70D9FF',
            borderRadius: '50%',
            border: '2px solid white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            zIndex: 10,
            '&::after': {
              content: '"▶"',
              color: 'white',
              fontSize: isMobile() ? '16px' : '12px',
              fontWeight: 'bold'
            }
          }}
        />
      </Box>
      
      {/* Временные метки */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 4,
        mt: 3 // Добавляем отступ сверху
      }}>
        <Typography variant="body2" sx={{ color: '#70D9FF', fontSize: '0.8rem' }}>
          {formatTime(startTime)}
        </Typography>
        
        <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem' }}>
          {formatTime(endTime - startTime)}
        </Typography>
        
        <Typography variant="body2" sx={{ color: '#70D9FF', fontSize: '0.8rem' }}>
          {formatTime(endTime)}
        </Typography>
      </Box>
      
      {/* Подсказка для перетаскивания */}
      {audioFile && !isAnalyzing && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#80FFB3', fontSize: '0.7rem', opacity: 0.8 }}>
            {t('trimmer.dragHint')}
          </Typography>
        </Box>
      )}
      
      {/* Кнопка обрезки */}
      {audioFile && !isAnalyzing && duration > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<ContentCut />}
            onClick={handleTrimAudio}
            disabled={isTrimming || startTime >= endTime}
            sx={{
              backgroundColor: '#80FFB3',
              color: '#283757',
              '&:hover': {
                backgroundColor: '#6BFF9E',
              },
              '&:disabled': {
                backgroundColor: 'rgba(128, 255, 179, 0.3)',
                color: 'rgba(40, 55, 87, 0.5)',
              }
            }}
          >
            {isTrimming ? trimProgress.stage || t('trimmer.trimming') : t('trimmer.trimAudio')}
          </Button>
        </Box>
      )}
      
      {/* Скрытый аудио элемент */}
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        onEnded={() => setIsPlaying(false)}
      />
    </Box>
  );
};

export default WaveformTrimmer; 